import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import PublicLayout from "@/components/layout/PublicLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * A19-P1.3 — Public referral landing page.
 * Path: /r/:token
 *
 * Loads the invitation context from `GET /api/referrals/{token}`. The backend
 * records the click event as a side-effect. The user is then offered a CTA
 * to continue to /register with the token pre-filled (passed via URL param
 * so Register.jsx picks it up).
 *
 * Errors:
 *   404 referral_token_not_found → "invalid link"
 *   410 referral_token_revoked   → "revoked"
 *   410 referral_token_expired   → "expired"
 *   200 + already_used=true      → still show landing but warn
 */
const ReferralLanding = () => {
  const { token } = useParams();
  const { t, setLang } = useLang();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    api.get(`/referrals/${encodeURIComponent(token)}`)
      .then((r) => {
        setState({ loading: false, data: r.data, error: null });
        // Auto-switch UI language to the invitee's hinted language so
        // the rest of the registration flow lands in the right locale.
        if (r.data?.target_language_hint) {
          setLang(r.data.target_language_hint);
        }
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail || "";
        setState({ loading: false, data: null, error: detail });
      });
  }, [token, setLang]);

  const goRegister = () => {
    const d = state.data || {};
    const params = new URLSearchParams({ token });
    if (d.target_profile_hint) params.set("profile", d.target_profile_hint);
    if (d.target_country_hint) params.set("country", d.target_country_hint);
    if (d.target_language_hint) params.set("lang", d.target_language_hint);
    navigate(`/register?${params.toString()}`);
  };

  return (
    <PublicLayout>
      <div className="tsw-section py-16 max-w-2xl">
        <Card data-testid="referral-landing-card">
          <CardHeader>
            <CardTitle className="text-3xl sm:text-4xl">
              {state.loading ? (
                <span className="inline-flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin" /> …
                </span>
              ) : state.error ? (
                <span className="inline-flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-6 h-6" />
                  {t("referrals.title")}
                </span>
              ) : (
                <span data-testid="referral-landing-heading">
                  {t("referrals.landing.heading", {
                    inviter: state.data?.inviter_first_name || "—",
                  })}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {state.loading && (
              <p className="text-slate-500">…</p>
            )}

            {!state.loading && state.error && (
              <div data-testid="referral-landing-error" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
                {state.error === "errors.referral_token_expired" && t("referrals.landing.expired")}
                {state.error === "errors.referral_token_revoked" && t("referrals.landing.revoked")}
                {state.error === "errors.referral_token_not_found" && t("referrals.errors.referral_token_not_found")}
                {!["errors.referral_token_expired", "errors.referral_token_revoked", "errors.referral_token_not_found"].includes(state.error) &&
                  extractErrorMessage({ response: { data: { detail: state.error } } }, state.error)}
                <div className="mt-4">
                  <Link to="/register" className="text-emerald-700 underline" data-testid="referral-landing-fallback-register">
                    {t("referrals.landing.goRegister")}
                  </Link>
                </div>
              </div>
            )}

            {!state.loading && !state.error && state.data && (
              <>
                <p className="text-lg text-slate-700" data-testid="referral-landing-subheading">
                  {t("referrals.landing.subheading")}
                </p>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  {state.data.target_profile_hint && (
                    <div className="rounded-lg border border-slate-200 p-3" data-testid="referral-landing-profile">
                      <div className="text-slate-500 text-xs">{t("referrals.landing.profileHint")}</div>
                      <div className="font-medium">{state.data.target_profile_hint}</div>
                    </div>
                  )}
                  {state.data.target_language_hint && (
                    <div className="rounded-lg border border-slate-200 p-3" data-testid="referral-landing-language">
                      <div className="text-slate-500 text-xs">{t("referrals.landing.languageHint")}</div>
                      <div className="font-medium uppercase">{state.data.target_language_hint}</div>
                    </div>
                  )}
                </div>

                {state.data.already_used && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="referral-landing-used">
                    <AlertTriangle className="inline w-4 h-4 me-1" />
                    {t("referrals.landing.used")}
                  </div>
                )}

                <Button
                  onClick={goRegister}
                  size="lg"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                  data-testid="referral-landing-continue-btn"
                  disabled={state.data.already_used}
                >
                  <CheckCircle2 className="w-5 h-5 me-2" />
                  {t("referrals.landing.cta")}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
};

export default ReferralLanding;
